import React, { FC, useContext, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { SettingsContext } from "context";
import {
  Switch,
  SwitchChangeEvent,
  TextArea,
} from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import axios from "axios";
import { Loader } from "@progress/kendo-react-indicators";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Report, Popup, IFrame } from "components";
import { PageType } from "types";
import { CallCenter } from "pages";
import { useLocation, useNavigate } from "react-router-dom";
import { Chat, Message, User } from "@progress/kendo-react-conversational-ui";
import {
  CONTOSO_COPILOT_DEMOS,
  CONTOSO_COPILOT_WITH_DASH,
  FSI_DEMOS,
  getIFrameUrl,
} from "common";

const { BlobBaseUrl } = window.config;

const INITIAL_GLOW_ARR = [
  { id: 1, value: true },
  { id: 2, value: false },
  { id: 3, value: false },
  { id: 4, value: false },
  { id: 5, value: false },
  { id: 6, value: false },
  { id: 7, value: false },
  { id: 8, value: false },
  { id: 9, value: false },
];

function upsert(array: any[], element: any) {
  const i = array.findIndex((_element) => _element?.id === element?.id);
  if (i > -1) array[i] = element; // (2)
  else array.push(element);
}

interface Props {
  showBeforeScript: boolean;
  showCallCenterBefore: boolean;
  showAfterSummary: boolean;
  showCallCenterAfter: boolean;
  showCustomerConversation: boolean;
}

const { APIUrl } = window.config;

export const CallCenterEditable: FC<Props> = (props) => {
  const { currentDemo } = useContext(SettingsContext);

  const [extractFromConversation, setExtractFromConversation] = useState("");
  const [inputScript, setInputScript] = useState("");
  const [includeKey, setIncludeKey] = useState(false);
  const [summary, setSummary] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(
    props.showCallCenterAfter ?? false
  );
  const [isEditConv, setIsEditConv] = useState(false);
  const [isEditScript, setIsEditScript] = useState(false);
  const [visible, setVisible] = useState(
    props.showCustomerConversation ?? false
  );
  const [showCallCenterBefore, setShowCallCenterBefore] = useState(
    props.showCallCenterBefore ?? true
  );
  const [showBeforeScript, setShowBeforeScript] = useState(
    props.showBeforeScript ?? false
  );
  const [showAfterSummary, setShowAfterSummary] = useState(
    props.showAfterSummary ?? false
  );
  const [glowBtn, setGlowBtn] = useState<any[]>(INITIAL_GLOW_ARR);
  const [messages, setMessages] = useState<Message[]>([]);
  const [authors, setAuthors] = useState<User[]>([]);

  useEffect(() => {
    setVisible(props.showCustomerConversation);
    setShowPopup(props.showCallCenterAfter);
    setShowCallCenterBefore(props.showCallCenterBefore);
    setShowAfterSummary(props.showAfterSummary);
    setShowBeforeScript(props.showBeforeScript);
  }, [props]);

  useEffect(() => {
    if (currentDemo?.guid) {
      setExtractFromConversation(
        includeKey
          ? currentDemo?.callCenterExtractFromConversationWithKey
          : currentDemo?.callCenterExtractFromConversationWithoutKey
      );
      setInputScript(currentDemo?.callCenterScript?.trim());
      setAuthors(
        currentDemo?.callCenterScript
          ?.trim()
          .split("\n")
          .map((a: string) => a.trim().split(":")[0])
          .map((id: string, index: number) => ({
            id: id,
            name: id,
            avatarUrl:
              index % 2 === 0
                ? `${BlobBaseUrl}${currentDemo?.callCenterAgentImage}`
                : `${BlobBaseUrl}${currentDemo?.callCenterCustomerImage}`,
          }))
      );
      const arrayForMessages = currentDemo?.callCenterScript
        ?.trim()
        .split("\n")
        .map((a: string) =>
          a
            .trim()
            .split(":")
            .map((a: string) => a.trim())
        );
      setMessages(
        arrayForMessages.map((line: string[]) => ({
          author: {
            id: line[0],
            name: line[0],
            avatarUrl:
              line[0] === arrayForMessages[0][0]
                ? `${BlobBaseUrl}${currentDemo?.callCenterAgentImage}`
                : `${BlobBaseUrl}${currentDemo?.callCenterCustomerImage}`,
          },
          text: line[1],
        })) as Message[]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDemo]);

  const generateSummary = (extractFromConv?: string) => {
    setIsLoading(true);
    setIsEditConv(false);
    setIsEditScript(false);
    axios
      .post(
        "https://func-callcenter-gensummary-prod-001.azurewebsites.net/api/callcenter_summary",
        {
          conversation: inputScript,
          question: extractFromConv ?? extractFromConversation,
        }
      )
      .then((res) => setSummary(res.data?.summary))
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  const onToggle = (e: SwitchChangeEvent) => {
    if (e.value && glowBtn[5].value) {
      setGlowBtn((old) => {
        upsert(old, { id: 6, value: false });
        upsert(old, { id: 7, value: true });

        return old;
      });
    }

    if (e.value) {
      setExtractFromConversation(
        currentDemo?.callCenterExtractFromConversationWithKey
      );
    } else {
      setExtractFromConversation(
        currentDemo?.callCenterExtractFromConversationWithoutKey
      );
    }
    setIncludeKey(e.value);
  };

  const location = useLocation();
  const navigate = useNavigate();

  return !currentDemo ? (
    <></>
  ) : (
    <div key={location.pathname} className={styles.container}>
      <h3>{currentDemo?.title} Call Center</h3>
      <div className={styles.tabContainer}>
        <div className={styles.tab}>
          <h5>Call Transcript</h5>
          <hr />

          <div
            style={{
              overflowY: "auto",
              flex: 1,
              padding: isEditScript ? "8px" : 0,
              fontSize: 15,
            }}
          >
            {isEditScript ? (
              <TextArea
                value={inputScript}
                onChange={(e) => setInputScript(e.value.trim())}
                autoSize={true}
                className={styles.textArea}
                disabled={!isEditScript}
              />
            ) : (
              <Chat
                className={styles.chat}
                user={authors[1]}
                messages={messages}
              />
            )}
          </div>

          <div className={styles.btnContainer}>
            <Button
              className={`${styles.btn}`}
              //  ${glowBtn[0].value && styles.glow}
              onClick={() => {
                setGlowBtn((old) => {
                  upsert(old, { id: 1, value: false });
                  upsert(old, { id: 2, value: true });

                  return old;
                });
                setIsEditScript(true);
              }}
              style={{
                backgroundColor: "transparent",
                border: "1px solid white",
              }}
              disabled={isEditScript}
            >
              Edit
            </Button>
            <Button
              className={`${styles.btn}`}
              //  ${glowBtn[1].value && styles.glow}
              onClick={() => {
                inputScript
                  .trim()
                  .split("\n")
                  .map((a) =>
                    a
                      .trim()
                      .split(":")
                      .map((a) => a.trim())
                  ).length > 1 && setIsEditScript(false);
                setAuthors(
                  inputScript
                    ?.trim()
                    .split("\n")
                    .map((a: string) => a.trim().split(":")[0])
                    .map((id: string, index: number) => ({
                      id: id,
                      name: id,
                      avatarUrl:
                        index % 2 === 0
                          ? `${BlobBaseUrl}${currentDemo?.callCenterAgentImage}`
                          : `${BlobBaseUrl}${currentDemo?.callCenterCustomerImage}`,
                    }))
                );
                const arrayForMessages = inputScript
                  ?.trim()
                  .split("\n")
                  .map((a: string) =>
                    a
                      .trim()
                      .split(":")
                      .map((a: string) => a.trim())
                  );
                if (arrayForMessages.length > 1)
                  setMessages(
                    arrayForMessages.map((line: string[]) => ({
                      author: {
                        id: line[0],
                        name: line[0],
                        avatarUrl:
                          line[0] === arrayForMessages[0][0]
                            ? `${BlobBaseUrl}${currentDemo?.callCenterAgentImage}`
                            : `${BlobBaseUrl}${currentDemo?.callCenterCustomerImage}`,
                      },
                      text: line[1],
                    })) as Message[]
                  );

                setGlowBtn((old) => {
                  upsert(old, { id: 2, value: false });
                  upsert(old, { id: 3, value: true });

                  return old;
                });
              }}
              disabled={!isEditScript}
            >
              Save
            </Button>
          </div>
        </div>
        <div className={styles.tab}>
          <div className={styles.tabHeader}>
            <h5>Extract from Conversation</h5>
            <div className={styles.switchContainer}>
              <h6>Machine Readable</h6>
              <Switch
                size="small"
                className={glowBtn[5].value && styles.glow}
                checked={includeKey}
                onChange={onToggle}
              />
            </div>
          </div>
          <hr />
          <TextArea
            value={extractFromConversation}
            onChange={(e) => setExtractFromConversation(e.value)}
            autoSize={true}
            className={styles.textArea}
            style={{
              overflowY: "auto",
              flex: 1,
              padding: "8px",
              fontSize: 15,
            }}
            disabled={!isEditConv}
          />
          <div className={styles.btnContainer} style={{ gap: 10 }}>
            <Button
              className={`${styles.btn}`}
              //  ${glowBtn[2].value && styles.glow}
              style={{
                backgroundColor: "transparent",
                border: "1px solid white",
              }}
              onClick={() => {
                if (glowBtn[2].value) {
                  setGlowBtn((old) => {
                    upsert(old, { id: 3, value: false });
                    upsert(old, { id: 4, value: true });

                    return old;
                  });
                }

                setIsEditConv(true);
              }}
              disabled={isEditConv}
            >
              Edit
            </Button>
            {/* <Button
              className={styles.btn}
              style={{ flex: 1 }}
              onClick={() => setVisible(true)}
            >
              Initiate Customer Call
            </Button> */}
            <Button
              className={`${styles.btn}`}
              //  ${glowBtn[3]?.value && styles.glow}
              onClick={() => {
                if (glowBtn[3].value) {
                  setGlowBtn((old) => {
                    upsert(old, { id: 4, value: false });
                    upsert(old, { id: 5, value: true });

                    return old;
                  });
                }
                setIsEditConv(false);
              }}
              disabled={!isEditConv}
            >
              Save
            </Button>
          </div>
        </div>
        <div className={styles.tab}>
          <h5>Generated Result</h5>
          <hr />
          <div className={styles.summaryContainer}>
            {isLoading ? (
              <Loader style={{ color: "white", alignSelf: "center" }} />
            ) : (
              <p style={{ fontSize: 15 }}>{summary.trim()}</p>
            )}
          </div>
          <div
            className={styles.btnContainer}
            style={{
              justifyContent: "center",
            }}
          >
            <Button
              className={`${styles.btn} ${
                (glowBtn[4].value || glowBtn[6].value) && styles.glow
              }`}
              style={{ width: "50%" }}
              onClick={() => {
                if (glowBtn[4].value) {
                  setGlowBtn((old) => {
                    upsert(old, { id: 5, value: false });
                    upsert(old, { id: 6, value: true });

                    return old;
                  });
                } else {
                  setGlowBtn((old) => {
                    upsert(old, { id: 7, value: false });
                    upsert(old, { id: 8, value: true });

                    return old;
                  });
                }
                generateSummary();
              }}
            >
              Generate Summary
            </Button>
          </div>
        </div>
      </div>

      <Popup
        showPopup={showCallCenterBefore}
        title="Call Center Report - Before"
        onClose={() => {
          // setShowCallCenterBefore(false);
          // setVisible(true);
          FSI_DEMOS.includes(currentDemo?.guid) ||
          CONTOSO_COPILOT_DEMOS.includes(currentDemo?.guid)
            ? navigate(`/${currentDemo.guid}/customer-conversation`)
            : navigate(`/${currentDemo.guid}/call-center`);
        }}
        className={styles.popup}
      >
        <Tooltip
          tooltipClassName={styles.tooltip}
          position="bottom"
          anchorElement="target"
          key={showBeforeScript.toString()}
        >
          <div>
            <img
              onClick={() => {
                if (showBeforeScript) {
                  FSI_DEMOS.includes(currentDemo?.guid) ||
                  CONTOSO_COPILOT_DEMOS.includes(currentDemo?.guid)
                    ? navigate(`/${currentDemo.guid}/customer-conversation`)
                    : navigate(`/${currentDemo.guid}/call-center`);
                  // setShowBeforeScript(false);
                  // setVisible(true);
                  // setShowCallCenterBefore(false);
                } else {
                  navigate(`/${currentDemo.guid}/call-center-before-script`);
                  // setShowBeforeScript(true);
                }
              }}
              title={
                showBeforeScript
                  ? FSI_DEMOS.includes(currentDemo?.guid) ||
                    CONTOSO_COPILOT_DEMOS.includes(currentDemo?.guid)
                    ? "Customer Conversation"
                    : "Call Center - Generate Summary"
                  : "Call Center Report - Script"
              }
              src={
                showBeforeScript
                  ? `${BlobBaseUrl}Arrow-B.png`
                  : `${BlobBaseUrl}Arrow-A.png`
              }
              alt="Arrow"
              className={styles.arrowA}
              style={{ right: 30 }}
            />
          </div>
        </Tooltip>
        {showBeforeScript ? (
          currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH ? (
            <IFrame
              pageTitle="Call Center - Before"
              pageType={PageType.CallCenterBeforeScript}
              url={getIFrameUrl(currentDemo.guid).callCenterBeforeScript}
            />
          ) : (
            <Report
              removeBackArrow
              pageTitle="Call Center - Before"
              pageType={PageType.CallCenter}
              id={
                FSI_DEMOS.includes(currentDemo?.guid)
                  ? "fa427432-b046-49c0-9aed-5bf17989463f"
                  : currentDemo.guid === "b7ffdc0e-ef2c-4f97-b6a2-e8d4ca2b962a"
                  ? "fa427432-b046-49c0-9aed-5bf17989463f"
                  : "793ce870-8a54-46c6-af0c-3c79365917b8"
              }
              name={"ReportSection623b64746831c0065bc0"}
            />
          )
        ) : currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH ? (
          <IFrame
            pageTitle="Call Center - Before"
            pageType={PageType.CallCenterBeforeSummary}
            url={getIFrameUrl(currentDemo.guid).callCenterBeforeSummary}
          />
        ) : (
          <Report
            removeBackArrow
            pageTitle="Call Center - Before"
            pageType={PageType.CallCenter}
            id={
              FSI_DEMOS.includes(currentDemo?.guid)
                ? "fa427432-b046-49c0-9aed-5bf17989463f"
                : currentDemo.guid === "b7ffdc0e-ef2c-4f97-b6a2-e8d4ca2b962a"
                ? "fa427432-b046-49c0-9aed-5bf17989463f"
                : "793ce870-8a54-46c6-af0c-3c79365917b8"
            }
            name={"ReportSectionda209890a7f0f9e42736"}
          />
        )}
      </Popup>

      <CallCenter
        generateSummary={generateSummary}
        visible={visible}
        setVisible={setVisible}
      />

      {/* <Tooltip
        tooltipClassName={styles.tooltip}
        position="bottom"
        anchorElement="target"
      >
        <div
          onClick={() => {
            // setShowPopup(true);
            navigate(`/${currentDemo.guid}/call-center-after-script`);
          }}
        >
          <img
            title="Call Center Report - After"
            src={`${BlobBaseUrl}Arrow-A.png`}
            alt="Arrow-A"
            className={styles.arrowA}
          />
        </div>
      </Tooltip> */}
      <Popup
        showPopup={showPopup}
        title="Call Center Report - After"
        onClose={() => {
          setShowPopup(false);
          navigate(`/${currentDemo.guid}/call-center`);
        }}
        className={styles.popup}
      >
        <Tooltip
          tooltipClassName={styles.tooltip}
          position="bottom"
          anchorElement="target"
          key={showAfterSummary.toString()}
        >
          <div>
            <img
              onClick={() => {
                if (showAfterSummary) {
                  setShowAfterSummary(false);
                  setShowPopup(false);
                  currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH
                    ? navigate(`/${currentDemo?.guid}/ceo-dashboard-after`)
                    : navigate(`/${currentDemo.guid}/landing-page`);
                } else {
                  navigate(`/${currentDemo.guid}/call-center-after-summary`);
                  // setShowAfterSummary(true);
                }
              }}
              title={
                showAfterSummary
                  ? currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH
                    ? "CEO Dashboard After"
                    : "Landing Page"
                  : "Call Center Report - Summary"
              }
              src={
                showAfterSummary
                  ? currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH
                    ? `${BlobBaseUrl}Arrow-9.png`
                    : CONTOSO_COPILOT_DEMOS.includes(currentDemo?.guid)
                    ? `${BlobBaseUrl}Arrow-5.png`
                    : `${BlobBaseUrl}Arrow-4.png`
                  : `${BlobBaseUrl}Arrow-D.png`
              }
              alt="Arrow"
              className={styles.arrowA}
              style={{ right: 30 }}
            />
          </div>
        </Tooltip>
        {showAfterSummary ? (
          currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH ? (
            <IFrame
              pageTitle="Call Center - After"
              pageType={PageType.CallCenterAfterSummary}
              url={getIFrameUrl(currentDemo.guid).callCenterAfterSummary}
            />
          ) : (
            <Report
              removeBackArrow
              id={
                FSI_DEMOS.includes(currentDemo?.guid)
                  ? "f3e373fc-4d0e-4555-a5b6-6997a460dbdd"
                  : currentDemo.guid === "b7ffdc0e-ef2c-4f97-b6a2-e8d4ca2b962a"
                  ? "f3e373fc-4d0e-4555-a5b6-6997a460dbdd"
                  : currentDemo?.callCenterReportID
              }
              name={"ReportSectionda209890a7f0f9e42736"}
              pageTitle="Call Center"
              pageType={PageType.CallCenter}
            />
          )
        ) : currentDemo?.guid === CONTOSO_COPILOT_WITH_DASH ? (
          <IFrame
            pageTitle="Call Center - After"
            pageType={PageType.CallCenterAfterScript}
            url={getIFrameUrl(currentDemo.guid).callCenterAfterScript}
          />
        ) : (
          <Report
            removeBackArrow
            id={
              FSI_DEMOS.includes(currentDemo?.guid)
                ? "f3e373fc-4d0e-4555-a5b6-6997a460dbdd"
                : currentDemo.guid === "b7ffdc0e-ef2c-4f97-b6a2-e8d4ca2b962a"
                ? "f3e373fc-4d0e-4555-a5b6-6997a460dbdd"
                : currentDemo?.callCenterReportID
            }
            name={currentDemo?.callCenterReportSectionName}
            pageTitle="Call Center"
            pageType={PageType.CallCenter}
          />
        )}
      </Popup>
    </div>
  );
};
